// ─── Plan configuration — single source of truth ─────────────────────────────
// Used by: PricingPage, Chatbot (plan gating), future billing integration

// User-facing plan labels are loaded from the shared translation database.
export const PLANS = [
  {
    id: 'root',
    nameKey: 'pricingPage.plan.root.name',
    tierKey: 'pricingPage.plan.root.tier',
    price: 0,
    currency: '€',
    period: null,
    descriptionKey: 'pricingPage.plan.root.description',
    badgeKey: 'pricingPage.plan.root.badge',
    ctaKey: 'pricingPage.cta.current',
    featureIds: [
      'hair_diagnostics',
      'product_recommendation',
      'hair_routine',
      'hair_tracker',
      'user_profile',
    ],
  },
  {
    id: 'care',
    nameKey: 'pricingPage.plan.care.name',
    tierKey: 'pricingPage.plan.care.tier',
    price: 4.99,
    currency: '€',
    period: 'month',
    descriptionKey: 'pricingPage.plan.care.description',
    badgeKey: 'pricingPage.plan.care.badge',
    ctaKey: 'pricingPage.cta.select',
    featureIds: [
      'hair_diagnostics',
      'product_recommendation',
      'hair_routine',
      'hair_tracker',
      'user_profile',
    ],
  },
  {
    id: 'pro',
    nameKey: 'pricingPage.plan.pro.name',
    tierKey: 'pricingPage.plan.pro.tier',
    price: 9.99,
    currency: '€',
    period: 'month',
    descriptionKey: 'pricingPage.plan.pro.description',
    badgeKey: 'pricingPage.plan.pro.badge',
    ctaKey: 'pricingPage.cta.upgrade',
    featureIds: [
      'hair_diagnostics',
      'product_recommendation',
      'hair_routine',
      'hair_tracker',
      'user_profile',
    ],
  },
]

// ─── All possible feature IDs ─────────────────────────────────────────────────
export const ALL_FEATURES = [
  {
    id: 'product_recommendation',
    labelKey: 'pricingPage.feature.productRecommendation',
  },
  { id: 'user_profile', labelKey: 'pricingPage.feature.userProfile' },
  { id: 'hair_diagnostics', labelKey: 'pricingPage.feature.hairDiagnostics' },
  { id: 'hair_tracker', labelKey: 'pricingPage.feature.hairTracker' },
  { id: 'hair_routine', labelKey: 'pricingPage.feature.hairRoutine' },
]

// ─── Feature gate map — which plans include which features ────────────────────
// Use this anywhere in the app to check access without importing the full PLANS
export const FEATURE_GATES = {
  product_recommendation: ['root', 'care', 'pro'],
  user_profile: ['root', 'care', 'pro'],
  hair_diagnostics: ['root', 'care', 'pro'],
  hair_tracker: ['root', 'care', 'pro'],
  hair_routine: ['root', 'care', 'pro'],
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const PLAN_ID_ALIASES = {
  root: ['root', 'free', 'basic', 'starter'],
  care: ['care', 'freemium', 'freemium plan', 'care plan'],
  pro: ['pro', 'premium', 'premium plan', 'pro plan'],
}

/** Normalize plan ids from backend or user state */
export const normalizePlanId = (planId) => {
  if (!planId) return 'root'
  if (typeof planId === 'object' && planId !== null)
    return normalizePlanId(planId.id)

  const normalized = String(planId).trim().toLowerCase()
  if (PLAN_ID_ALIASES.root.includes(normalized)) return 'root'
  if (PLAN_ID_ALIASES.care.includes(normalized)) return 'care'
  if (PLAN_ID_ALIASES.pro.includes(normalized)) return 'pro'
  return normalized
}

/** Check if a plan has access to a feature */
export const planHasFeature = (planId, featureId) => {
  return FEATURE_GATES[featureId]?.includes(normalizePlanId(planId)) ?? false
}

/** Get the next plan up from the current one */
export const getUpgradePath = (currentPlanId) => {
  const index = PLANS.findIndex((p) => p.id === normalizePlanId(currentPlanId))
  if (index === -1 || index === PLANS.length - 1) return null
  return PLANS[index + 1]
}

/** Get full plan object by id */
export const getPlanById = (planId) =>
  PLANS.find((p) => p.id === normalizePlanId(planId)) ?? PLANS[0]

/** Get all feature ids available on a plan */
export const getPlanFeatures = (planId) =>
  Object.entries(FEATURE_GATES)
    .filter(([, plans]) => plans.includes(normalizePlanId(planId)))
    .map(([featureId]) => featureId)

// ─── All possible features (used to render full comparison grid) ──────────────
